"use client";
import { Dialog } from "@base-ui/react/dialog";
import { XIcon } from "lucide-react";
import type { BirthDateResponse } from "../../_components/types";

interface ResultDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  result: BirthDateResponse | null;
}

const PinkBalloon = () => (
  <svg
    fill="none"
    height="96"
    viewBox="0 0 72 96"
    width="72"
    xmlns="http://www.w3.org/2000/svg"
  >
    <title>Balloon2</title>
    <ellipse cx="36" cy="38" fill="#F9A8C9" rx="28" ry="34" />
    <ellipse
      cx="26"
      cy="24"
      fill="#FBC8DC"
      opacity="0.7"
      rx="8"
      ry="6"
      transform="rotate(-20 26 24)"
    />
    <path
      d="M36 72 C34 76 38 80 36 84 C34 88 38 92 36 96"
      fill="none"
      stroke="#2D6B5E"
      strokeLinecap="round"
      strokeWidth="2.5"
    />
    <path
      d="M32 72 Q36 74 40 72"
      fill="none"
      stroke="#2D6B5E"
      strokeLinecap="round"
      strokeWidth="2"
    />
  </svg>
);

const BlueBalloon = () => (
  <svg
    fill="none"
    height="96"
    viewBox="0 0 72 96"
    width="72"
    xmlns="http://www.w3.org/2000/svg"
  >
    <title>Balloon</title>
    <ellipse cx="36" cy="38" fill="#93C5E8" rx="28" ry="34" />
    <ellipse
      cx="26"
      cy="24"
      fill="#BFE0F5"
      opacity="0.7"
      rx="8"
      ry="6"
      transform="rotate(-20 26 24)"
    />
    <path
      d="M36 72 C34 76 38 80 36 84 C34 88 38 92 36 96"
      fill="none"
      stroke="#2D6B5E"
      strokeLinecap="round"
      strokeWidth="2.5"
    />
    <path
      d="M32 72 Q36 74 40 72"
      fill="none"
      stroke="#2D6B5E"
      strokeLinecap="round"
      strokeWidth="2"
    />
  </svg>
);

export const ResultDialog = ({
  open,
  onOpenChange,
  result,
}: ResultDialogProps) => {
  return (
    <Dialog.Root onOpenChange={onOpenChange} open={open}>
      <Dialog.Portal>
        <Dialog.Backdrop className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm transition-opacity" />
        <Dialog.Popup className="fixed top-1/2 left-1/2 z-50 flex w-[90vw] max-w-md -translate-x-1/2 -translate-y-1/2 flex-col rounded-3xl bg-white shadow-2xl outline-none overflow-hidden">
          <div className="flex items-center justify-between px-6 py-4">
            <Dialog.Title className="text-xl font-bold text-gray-900">
              Doğum Tarihi Hesaplama
            </Dialog.Title>
            <Dialog.Close className="rounded-full p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600">
              <XIcon className="size-5" />
            </Dialog.Close>
          </div>

          <div className="bg-linear-to-b from-blue-100 to-white flex items-center justify-center gap-4 py-8">
            <PinkBalloon />
            <div className="flex flex-col items-center justify-center rounded-full bg-[#3b82c4] size-36 text-white shrink-0">
              <span className="text-5xl font-bold leading-none">
                {result?.daysRemaining ?? "-"}
              </span>
              <span className="text-sm mt-1">
                {result?.isOverdue
                  ? "Gün Geçti"
                  : (result?.countdownSuffix ?? "Gün Kaldı")}
              </span>
            </div>
            <BlueBalloon />
          </div>

          {result ? (
            <div className="px-6 pt-2 pb-4 flex flex-col gap-2">
              <div className="flex items-center justify-between border border-gray-200 rounded-2xl px-4 py-3">
                <span className="text-gray-700 text-sm">Doğum Tarihi</span>
                <span className="text-gray-900 font-bold text-sm">
                  {result.dueDateText}
                </span>
              </div>
              <div className="flex items-center justify-between border border-gray-200 rounded-2xl px-4 py-3">
                <span className="text-gray-700 text-sm">Gebelik Haftası</span>
                <span className="text-gray-900 font-bold text-sm">
                  {result.pregnancyWeek}
                </span>
              </div>
              <div className="flex items-center justify-between border border-gray-200 rounded-2xl px-4 py-3">
                <span className="text-gray-700 text-sm">
                  Gebelik Gün Sayısı
                </span>
                <span className="text-gray-900 font-bold text-sm">
                  {result.pregnancyDayCount}
                </span>
              </div>
            </div>
          ) : (
            <p className="px-6 py-4 text-sm text-gray-500">Sonuç bulunamadı.</p>
          )}

          <div className="px-6 pb-6 pt-2">
            <Dialog.Close className="w-full rounded-full bg-[#3b82c4] py-4 text-base font-semibold text-white transition-colors hover:bg-[#2d6ea8]">
              Tekrar Hesapla
            </Dialog.Close>
          </div>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
